import {
  AriaPopover,
  AriaSeparator,
  HStack,
  KdsAvatar,
  KdsListBox,
  KdsListBoxItem,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import {
  RiAccountCircleLine,
  RiCheckboxCircleLine,
  RiLogoutCircleRLine,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useAtomValue, useSetAtom } from "jotai";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks";
import {
  accountsAtom,
  currentAccountAtom,
  currentAccountIdAtom,
  userAtom,
} from "@/store";
import { type HeaderPopoverProps } from "./HeaderPopover.types";

export const HeaderPopover = ({ avatarUrl }: HeaderPopoverProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { disconnect } = useAuth();
  const avatarRef = useRef<HTMLButtonElement>(null);

  const userAtomValue = useAtomValue(userAtom);
  const accounts = useAtomValue(accountsAtom);
  const currentAccount = useAtomValue(currentAccountAtom);

  const setCurrentAccountId = useSetAtom(currentAccountIdAtom);

  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const fullName = [userAtomValue?.firstName, userAtomValue?.lastName].join(
    " "
  );

  const handleAccountSwitch = (accountId: string) => {
    setCurrentAccountId(accountId);
    setIsAvatarMenuOpen(false);
    void navigate("/");
  };

  return (
    <>
      <button
        className="cursor-pointer hover:bg-surface-second rounded-br-sm"
        type="button"
        ref={avatarRef}
        onClick={() => setIsAvatarMenuOpen((prev) => !prev)}
      >
        <HStack
          align="center"
          justify="flex-start"
          gap="sp-xs"
          px="sp-xs2"
          py="sp-xs3"
        >
          <KdsAvatar size="sm" src={avatarUrl} type="image" name={fullName} />

          <VStack>
            <KdsText size="subtitle-xs">{fullName}</KdsText>
            <KdsText size="caption-sm" color="text-bodyvariant">
              {currentAccount?.name}
            </KdsText>
          </VStack>
        </HStack>
      </button>

      <AriaPopover
        triggerRef={avatarRef}
        isOpen={isAvatarMenuOpen}
        onOpenChange={setIsAvatarMenuOpen}
        placement="bottom end"
      >
        <KdsListBox
          aria-label="User menu"
          selectionMode="single"
          selectedKeys={currentAccount ? [currentAccount.id] : []}
        >
          {accounts.map((account) => (
            <KdsListBoxItem
              key={account.id}
              id={account.id}
              type="avatar"
              avatarProps={{
                name: account.name,
                type: "image",
                src: account?.logoUrl || "",
              }}
              textValue={account.name}
              onPress={() => handleAccountSwitch(account.id)}
            >
              {account.name}
            </KdsListBoxItem>
          ))}

          <AriaSeparator />

          <KdsListBoxItem
            id="userProfile"
            type="icon"
            icon={RiAccountCircleLine}
            textValue={t("header.popover.userProfile")}
            onPress={() => {
              void navigate("/account");
              setIsAvatarMenuOpen(false);
            }}
          >
            {t("header.popover.userProfile")}
          </KdsListBoxItem>

          <KdsListBoxItem
            id="manageCookies"
            type="icon"
            icon={RiCheckboxCircleLine}
            textValue={t("header.popover.manageCookies")}
            onPress={() => {
              window?.axeptioSDK?.openCookies();
              setIsAvatarMenuOpen(false);
            }}
          >
            {t("header.popover.manageCookies")}
          </KdsListBoxItem>

          <KdsListBoxItem
            id="logout"
            type="icon"
            icon={RiLogoutCircleRLine}
            textValue={t("header.popover.logout")}
            onPress={() => {
              void navigate("/");
              disconnect();
              setIsAvatarMenuOpen(false);
            }}
          >
            {t("header.popover.logout")}
          </KdsListBoxItem>
        </KdsListBox>
      </AriaPopover>
    </>
  );
};
